<template>
  <b-row>
    <b-col sm="12">

      <b-card body-class="py-2">
        <section class="list-view-heading">
          <ModuleViewHeading
            :module-id="access.module_id"
            :profile-id="access.profile_id"
            :reference="access.view_reference"
            summary-only
          />
          <div class="list-view-heading__controls">
            <div class="list-view-heading__actions">
              <TabHeaderActions />
            </div>
          </div>
        </section>
      </b-card>

      <div class="reports-design-workspace">
        <div class="reports-design-selector-panel">
          <b-row>
            <b-col md="4">
              <FindObject render="search"
                          type="reports"
                          @select-object="loadReports($event)"
                          :valueID="crud.form.id"
                          :extra-filter-data="{ modules: activeModuleIds }"/>
            </b-col>

            <b-col md="1" v-if="crud.form.reports && !crud.form.reports.only_detail">
              <b-form-group label="ID" v-if="contentRender!='[[no-config]]' && contentRender!='[[no-report]]'">
                <b-form-input type="number"
                              size="sm"
                              v-model="filter.id"
                              required
                              placeholder="Ingresar un ID"
                              @keyup.enter="filterReports()">
                </b-form-input>
              </b-form-group>
            </b-col>
            <b-col md="1" v-if="contentRender!='[[no-config]]' && contentRender!='[[no-report]]'">
              <b-button variant="outline-dark"
                        class="reports-design-button-filter"
                        size="sm"
                        v-b-tooltip.hover
                        title="Aplicar filtro"
                        @click="filterReports()">
                <AppIcon name="filter" class="mr-1" />Filtrar
              </b-button>
            </b-col>
          </b-row>
        </div>

        <b-card v-if="!crud.form.reports" class="reports-design-empty-card">
          <EmptyState icon="search" title="Buscá y seleccioná un reporte" description="Elegí un reporte para configurar sus variables, diseño, configuración y vista previa." />
        </b-card>

        <b-row v-else class="reports-design-layout">
          <b-col lg="3">     
            <b-tabs>
              <b-tab title="Disponibles" active>
                <b-row>           
                  <b-col md="12">                                                  
                    <JsonAccordion v-if="jsonFilter" :data="jsonFilter" class="reports-design-pre-json" />
                    <EmptyState v-else icon="braces" title="No hay datos para mostrar" description="Cargá un ID y filtrá para ver las variables disponibles del registro." />
                  </b-col>
                </b-row>
              </b-tab>
              <b-tab title="Utilizados">
                <div class="reports-design-used-accordion">
                  <b-card no-body class="reports-design-used-card" v-if="canEditPdf">
                    <b-button block variant="light" class="reports-design-used-header" v-b-toggle.reports-used-pdf>
                      <AppIcon name="file-earmark-text" /> PDF
                    </b-button>
                    <b-collapse id="reports-used-pdf" accordion="reports-used" :visible="selectedFormat === 'pdf'">
                      <b-card-body>
                        <pre v-if="variablesUsedPdf" class="reports-design-pre-json reports-design-json-code" v-html="formatJson(variablesUsedPdf)"></pre>
                        <EmptyState v-else icon="file-earmark-text" title="Aún no se utilizó ninguna variable" description="Las variables que uses en el diseño PDF van a aparecer acá." />
                      </b-card-body>
                    </b-collapse>
                  </b-card>

                  <b-card no-body class="reports-design-used-card" v-if="canEditEscpos">
                    <b-button block variant="light" class="reports-design-used-header" v-b-toggle.reports-used-escpos>
                      <AppIcon name="receipt" /> ESC/POS
                    </b-button>
                    <b-collapse id="reports-used-escpos" accordion="reports-used" :visible="selectedFormat === 'escpos'">
                      <b-card-body>
                        <pre v-if="variablesUsedEscpos" class="reports-design-pre-json reports-design-json-code" v-html="formatJson(variablesUsedEscpos)"></pre>
                        <EmptyState v-else icon="receipt" title="Aún no se utilizó ninguna variable" description="Las variables que uses en el diseño ESC/POS van a aparecer acá." />
                      </b-card-body>
                    </b-collapse>
                  </b-card>
                </div>
              </b-tab>
              <b-tab title="Ayuda">
                <div class="reports-design-help" role="tablist">

                  <div class="reports-design-help-section">
                    <div class="reports-design-help-title reports-design-help-toggle" v-b-toggle.help-variables>
                      <AppIcon name="braces" /> Variables
                      <AppIcon name="chevron-down" class="reports-design-help-chevron" />
                    </div>
                    <b-collapse id="help-variables" accordion="help-accordion" visible>
                      <div class="reports-design-help-item">
                        <code v-pre>{{variable}}</code>
                        <span>Insertar una variable escribiendo el nombre entre dos llaves.</span>
                      </div>
                      <div class="reports-design-help-item">
                        <code v-pre>{{customer.name}}</code>
                        <span>Acceder a propiedades hijas usando <strong>punto</strong> hasta la última propiedad disponible.</span>
                      </div>
                      <div class="reports-design-help-note">
                        Dejá un espacio antes de la llave de apertura y después de la llave de cierre cuando lo uses dentro del template.
                      </div>
                    </b-collapse>
                  </div>

                  <div class="reports-design-help-section">
                    <div class="reports-design-help-title reports-design-help-toggle" v-b-toggle.help-formats>
                      <AppIcon name="filter" /> Formatos
                      <AppIcon name="chevron-down" class="reports-design-help-chevron" />
                    </div>
                    <b-collapse id="help-formats" accordion="help-accordion">
                      <div class="reports-design-help-item">
                        <code v-pre>{{date|date}}</code>
                        <span>Aplicar un formato agregando <strong>|</strong> después del nombre de la variable.</span>
                      </div>
                      <div class="reports-design-help-format-list">
                        <div class="reports-design-help-format">
                          <code>date</code>
                          <span>Fechas.</span>
                        </div>
                        <div class="reports-design-help-format">
                          <code>currency</code>
                          <span>Importes monetarios.</span>
                        </div>
                        <div class="reports-design-help-format">
                          <code>int</code>
                          <span>Números enteros.</span>
                        </div>
                        <div class="reports-design-help-format">
                          <code>decimal</code>
                          <span>Números decimales.</span>
                        </div>
                        <div class="reports-design-help-format">
                          <code>pointSales</code>
                          <span>Punto de venta.</span>
                        </div>
                        <div class="reports-design-help-format">
                          <code>numberInvoice</code>
                          <span>Número de comprobante.</span>
                        </div>
                        <div class="reports-design-help-format">
                          <code>si-no</code>
                          <span>Valores booleanos.</span>
                        </div>
                        <div class="reports-design-help-format">
                          <code>image</code>
                          <span>Renderizado de imágenes.</span>
                        </div>
                      </div>
                      <div class="reports-design-help-subtitle">Formatos avanzados</div>
                      <div class="reports-design-help-item">
                        <code>concatLeft[valor]</code>
                        <span>Concatena texto a la izquierda.</span>
                      </div>
                      <div class="reports-design-help-item">
                        <code>concatRight[valor]</code>
                        <span>Concatena texto a la derecha.</span>
                      </div>
                      <div class="reports-design-help-item">
                        <code>tablePriceList[&lt;fields&gt;]</code>
                        <span>Tabla de precios con encabezado. Campos por defecto: <strong>code, name, desc, price, days_update</strong>.</span>
                      </div>
                      <div class="reports-design-help-item">
                        <code>tablePriceListWithoutHeader[&lt;fields&gt;]</code>
                        <span>Tabla de precios sin encabezado.</span>
                      </div>
                      <div class="reports-design-help-item">
                        <code v-pre>{{price_unit|eval(field['price_unit']+(field['amount_iva']/field['quantity']))}}</code>
                        <span><strong>eval</strong> ejecuta cálculos. Usá <strong>field['campo']</strong> para leer valores del registro y evitá espacios en la fórmula.</span>
                      </div>
                      <div class="reports-design-help-item">
                        <code>if(true,false,tag,class)</code>
                        <span>Condicional simple. Puede devolver texto, un campo, envolverlo en una etiqueta y aplicar una clase.</span>
                      </div>
                      <div class="reports-design-help-item">
                        <code>field-valid(compare,true,false,format,tag,class)</code>
                        <span>Condicional con comparación y formato de salida opcional.</span>
                      </div>
                    </b-collapse>
                  </div>

                  <div class="reports-design-help-section">
                    <div class="reports-design-help-title reports-design-help-toggle" v-b-toggle.help-escpos>
                      <AppIcon name="printer" /> Comandos ESC/POS
                      <AppIcon name="chevron-down" class="reports-design-help-chevron" />
                    </div>
                    <b-collapse id="help-escpos" accordion="help-accordion">
                      <div class="reports-design-help-item">
                        <code v-pre>[[align=left]] / [[align=center]] / [[align=right]]</code>
                        <span>Alinean el texto del ticket. Se escriben antes de la línea o bloque que querés acomodar. También podés usar <code v-pre>[[left]]</code>, <code v-pre>[[center]]</code> y <code v-pre>[[right]]</code>.</span>
                      </div>
                      <div class="reports-design-help-item">
                        <code v-pre>[[bold]]texto[[/bold]]</code>
                        <span>Activa negrita para el tramo indicado. Usalo para destacar el total, encabezados o subtítulos. Alias: <code v-pre>[[bold=1]]</code> / <code v-pre>[[bold:on]]</code> para activar, <code v-pre>[[bold=0]]</code> / <code v-pre>[[bold:off]]</code> para desactivar.</span>
                      </div>
                      <div class="reports-design-help-item">
                        <code v-pre>etiqueta[[col]]valor</code>
                        <span>Separador de dos columnas. El texto de la izquierda queda pegado al margen izquierdo y el valor se alinea al margen derecho, calculando el espacio automáticamente según el ancho del ticket configurado.</span>
                      </div>
                      <div class="reports-design-help-item">
                        <code v-pre>[[big]]texto[[/big]]</code>
                        <span>Dobla el tamaño del texto (ancho + alto). Ideal para el total. Alias: <code v-pre>[[size=2]]</code>. También podés usar <code v-pre>[[size=dh]]</code> (solo alto doble) o <code v-pre>[[size=dw]]</code> (solo ancho doble). Volvé al tamaño normal con <code v-pre>[[/big]]</code>, <code v-pre>[[/size]]</code> o <code v-pre>[[size=1]]</code>.</span>
                      </div>
                      <div class="reports-design-help-item">
                        <code v-pre>[[feed=N]]</code>
                        <span>Avanza N líneas en el papel antes de continuar. Útil para separar secciones o dejar espacio antes del corte. Ejemplo: <code v-pre>[[feed=4]]</code>.</span>
                      </div>
                      <div class="reports-design-help-item">
                        <code v-pre>[[cut]]</code>
                        <span>Corte completo del papel. Colocalo al final del template. Si el corte sale muy arriba, agregá un <code v-pre>[[feed=N]]</code> antes. Alias: <code v-pre>[[cut=full]]</code>. También existe <code v-pre>[[cut=partial]]</code> para corte parcial (el papel queda unido por un borde).</span>
                      </div>
                      <div class="reports-design-help-item">
                        <code v-pre>[[no-init]]</code>
                        <span>Omite el comando de inicialización de la impresora (<code>ESC @</code>). Usalo al inicio del template si tu impresora genera un margen superior en blanco al inicializarse. Sin este tag, la impresora se resetea en cada impresión para evitar estado residual de trabajos anteriores.</span>
                      </div>
                      <div class="reports-design-help-note">
                        Los comandos ESC/POS no son HTML. La vista previa los interpreta y el servicio local los recibe como comandos de impresora.
                      </div>
                    </b-collapse>
                  </div>

                  <div class="reports-design-help-section">
                    <div class="reports-design-help-title reports-design-help-toggle" v-b-toggle.help-details>
                      <AppIcon name="list-nested" /> Detalles
                      <AppIcon name="chevron-down" class="reports-design-help-chevron" />
                    </div>
                    <b-collapse id="help-details" accordion="help-accordion">
                      <div class="reports-design-help-item">
                        <code v-pre>{{details|arrayStart}}</code>
                        <span>Iniciar un bloque repetible usando la colección de detalles.</span>
                      </div>
                      <div class="reports-design-help-item">
                        <code v-pre>{{-|arrayStart}}</code>
                        <span>Usar cuando el dato raíz ya es una lista.</span>
                      </div>
                      <div class="reports-design-help-item">
                        <code v-pre>{{details|arrayEnd}}</code>
                        <span>Cerrar el bloque repetible.</span>
                      </div>
                      <div class="reports-design-help-note">
                        Dentro del bloque repetible, las variables parten desde la primera propiedad hija declarada en la repetición.
                      </div>
                    </b-collapse>
                  </div>

                </div>
              </b-tab>                
            </b-tabs>
          </b-col>

          <b-col lg="9">
            <b-tabs>
              <b-tab title="Diseño" active>
                <b-card no-body class="mt-2">
                  <b-tabs v-model="activeDesignTab" pills card vertical class="reports-design-vertical-tabs">
                    <b-tab title="PDF" v-if="canEditPdf">
                      <b-card-text>
                        <b-row>
                          <b-col lg="12" class="mb-2">
                            <b-button variant="warning" class="pull-left" size="sm" @click="restoreDefaultReports('pdf')" v-if="!crud.form.blocked && hasDefaultDesign('pdf')">
                              <AppIcon name="arrow-counterclockwise" class="mr-1" />Restaurar PDF
                            </b-button>
                            <b-button variant="dark" class="pull-right" size="sm" @click="saveReports('pdf')">
                              <AppIcon name="save" class="mr-1" />Guardar PDF
                            </b-button>
                          </b-col>
                        </b-row>
                        <prism-editor v-model="crud.form.content" language="html" class="crud-reports-design-code-editor"></prism-editor>
                      </b-card-text>
                    </b-tab>
                    <b-tab title="ESC/POS" v-if="canEditEscpos">
                      <b-card-text>
                        <b-row>
                          <b-col lg="12" class="mb-2">
                            <b-button variant="warning" class="pull-left" size="sm" @click="restoreDefaultReports('escpos')" v-if="!crud.form.blocked_escpos && hasDefaultDesign('escpos')">
                              <AppIcon name="arrow-counterclockwise" class="mr-1" />Restaurar ESC/POS
                            </b-button>
                            <b-button variant="dark" class="pull-right" size="sm" @click="saveReports('escpos')">
                              <AppIcon name="save" class="mr-1" />Guardar ESC/POS
                            </b-button>
                          </b-col>
                        </b-row>
                        <prism-editor v-model="crud.form.content_escpos" language="text" class="crud-reports-design-code-editor"></prism-editor>
                      </b-card-text>
                    </b-tab>
                  </b-tabs>
                </b-card>
              </b-tab>   

              <b-tab title="Configuraciones">
                  <b-card no-body>
                    <b-tabs v-model="activeConfigurationTab" pills card vertical>
                      <b-tab title="PDF" active v-if="canEditPdf">
                        <b-card-text>

                          <b-row>
                            <b-col lg="12">
                              <b-form-group label="Medidas">
                                <b-form-select v-model="crud.form.page" :options="arr.page"></b-form-select>
                              </b-form-group>
                            </b-col>
                            <b-col lg="12">
                              <b-form-group label="Orientación">
                                <b-form-select v-model="crud.form.orientation" :options="arr.orientation"></b-form-select>
                              </b-form-group>
                            </b-col>
                            <b-col lg="12">
                              <b-form-group label="Fuente por Defecto">
                                <b-form-select v-model="crud.form.font" :options="arr.fonts"></b-form-select>
                              </b-form-group>
                            </b-col>  
                            <b-col md="12">
                              <b-form-checkbox v-model="crud.form.blocked" 
                                                switch 
                                                size="sm" 
                                                class="pull-left">
                                Bloquea la configuración del reporte
                              </b-form-checkbox> 
                            </b-col>                                       
                            <b-col lg="12" class="mt-3">
                              <b-button variant="dark" class="pull-right" size="sm" @click="saveReportsConfiguration('pdf')">
                                <AppIcon name="save" class="mr-1" />Guardar PDF
                              </b-button>
                            </b-col>
                          </b-row>                          
                          
                        </b-card-text>
                      </b-tab>
                      <b-tab title="ESC/POS" v-if="canEditEscpos">
                        <b-card-text>

                          <b-row>
                            <b-col lg="12">
                              <div class="reports-design-config-section-title">Estructura</div>
                            </b-col>
                            <b-col md="6">
                              <b-form-group label="Ancho del ticket">
                                <b-form-select v-model="crud.form.escpos_ticket_width_mm" :options="arr.escposWidth"></b-form-select>
                              </b-form-group>
                            </b-col>
                            <b-col md="3">
                              <b-form-group label="Margen superior (mm)">
                                <b-form-input type="number" size="sm" min="0" step="1" v-model="crud.form.escpos_margin_top_mm"></b-form-input>
                              </b-form-group>
                            </b-col>
                            <b-col md="3">
                              <b-form-group label="Margen inferior (mm)">
                                <b-form-input type="number" size="sm" min="0" step="1" v-model="crud.form.escpos_margin_bottom_mm"></b-form-input>
                              </b-form-group>
                            </b-col>
                            <b-col md="3">
                              <b-form-group label="Margen izquierdo (mm)">
                                <b-form-input type="number" size="sm" min="0" step="1" v-model="crud.form.escpos_margin_left_mm"></b-form-input>
                              </b-form-group>
                            </b-col>
                            <b-col md="3">
                              <b-form-group label="Margen derecho (mm)">
                                <b-form-input type="number" size="sm" min="0" step="1" v-model="crud.form.escpos_margin_right_mm"></b-form-input>
                              </b-form-group>
                            </b-col>
                            <b-col md="6">
                              <b-form-group label="Alineación base">
                                <b-form-select v-model="crud.form.escpos_alignment" :options="arr.escposAlignment"></b-form-select>
                              </b-form-group>
                            </b-col>

                            <b-col md="12" class="mt-2">
                              <b-form-checkbox v-model="crud.form.blocked_escpos"
                                                switch
                                                size="sm"
                                                class="pull-left">
                                Bloquea la configuración ESC/POS del reporte
                              </b-form-checkbox>
                            </b-col>
                            <b-col lg="12" class="mt-3">
                              <b-button variant="dark" class="pull-right" size="sm" @click="saveReportsConfiguration('escpos')">
                                <AppIcon name="save" class="mr-1" />Guardar ESC/POS
                              </b-button>
                            </b-col>
                          </b-row>

                        </b-card-text>
                      </b-tab>
                      <b-tab title="Generales" v-if="isVersionAlternative">
                        <b-card-text>

                          <b-row>
                            <b-col lg="12">
                              <b-form-group label="Nombre">
                                <b-form-input type="text"
                                              size="sm"
                                              v-model="crud.form.name"
                                              required
                                              placeholder="Ingresar un nombre">
                                </b-form-input>                                
                              </b-form-group>
                            </b-col>
                            <b-col md="12">
                              <b-form-group label="Activo">
                                <b-form-checkbox v-model="crud.form.active" 
                                                  switch 
                                                  size="sm" 
                                                  class="pull-left">
                                  Disponible para la búsqueda de reportes en la transacción correspondiente
                                </b-form-checkbox> 
                              </b-form-group>
                            </b-col>
                            <b-col lg="12" class="mt-3">
                              <b-button variant="dark" class="pull-right" size="sm" @click="saveReportsConfiguration('general')">
                                <AppIcon name="save" class="mr-1" />Guardar Generales
                              </b-button>
                            </b-col>
                          </b-row>

                        </b-card-text>
                      </b-tab>                      
                    </b-tabs>
                  </b-card>

              </b-tab>

              <b-tab title="Vista Previa" @click="renderReportsPreview()">
                <b-card no-body class="mt-2">
                  <b-tabs v-model="activePreviewTab" pills card vertical class="reports-design-vertical-tabs">
                    <b-tab title="PDF" v-if="canEditPdf" active @click="renderReports()">
                      <b-card-text>
                        <b-row>
                          <b-col lg="12" class="mb-2">
                            <b-button variant="outline-dark" class="pull-right" size="sm" @click="generateReports()">
                              <AppIcon name="printer" class="mr-1" />Imprimir PDF
                            </b-button>
                          </b-col>
                          <b-col lg="12">
                            <EmptyState v-if="getFormatContentHTML()=='[[no-config]]'"
                                        icon="code"
                                        title="Reporte sin configurar"
                                        description="Guardá el código fuente para visualizarlo." />
                            <EmptyState v-if="getFormatContentHTML()=='[[no-report]]'"
                                        icon="reports"
                                        title="No se seleccionó el reporte"
                                        description="Seleccioná el reporte a editar." />
                            <EmptyState v-if="getFormatContentHTML()=='[[no-data]]' && !filter.id"
                                        icon="filter"
                                        title="Cargá un ID y filtrá"
                                        description="Filtrá para visualizar el comprobante con un ID que tenga registros generados." />
                            <EmptyState v-if="getFormatContentHTML()=='[[no-data]]' && filter.id"
                                        icon="search"
                                        title="No se encontraron datos"
                                        description="Filtrá por un ID con registros generados." />

                            <div v-if="getFormatContentHTML()!='[[no-config]]' && getFormatContentHTML()!='[[no-report]]' && getFormatContentHTML()!='[[no-data]]'">
                              <div class="crud-reports-design-preview">
                                <div v-html="getFormatContentHTML()" class="crud-reports-design-preview-html"></div>
                              </div>
                            </div>
                          </b-col>
                        </b-row>
                      </b-card-text>
                    </b-tab>

                    <b-tab title="ESC/POS" v-if="canEditEscpos" :active="!canEditPdf" @click="renderReportsEscpos()">
                      <b-card-text>
                        <b-row>
                          <b-col lg="12" class="mb-2">
                            <b-button variant="outline-dark" class="pull-right" size="sm" @click="generateReportsEscpos()">
                              <AppIcon name="printer" class="mr-1" />Imprimir ESC/POS
                            </b-button>
                          </b-col>
                          <b-col lg="12">
                            <EmptyState v-if="getFormatContentEscpos()=='[[no-config]]'"
                                        icon="code"
                                        title="Reporte sin configurar"
                                        description="Guardá el código fuente para visualizarlo." />
                            <EmptyState v-if="getFormatContentEscpos()=='[[no-report]]'"
                                        icon="reports"
                                        title="No se seleccionó el reporte"
                                        description="Seleccioná el reporte a editar." />
                            <EmptyState v-if="getFormatContentEscpos()=='[[no-data]]' && !filter.id"
                                        icon="filter"
                                        title="Cargá un ID y filtrá"
                                        description="Filtrá para visualizar el comprobante con un ID que tenga registros generados." />
                            <EmptyState v-if="getFormatContentEscpos()=='[[no-data]]' && filter.id"
                                        icon="search"
                                        title="No se encontraron datos"
                                        description="Filtrá por un ID con registros generados." />

                            <div v-if="getFormatContentEscpos()!='[[no-config]]' && getFormatContentEscpos()!='[[no-report]]' && getFormatContentEscpos()!='[[no-data]]'" class="crud-reports-design-preview crud-reports-design-preview-escpos-shell" :style="getEscposPreviewShellStyle()">
                              <div class="crud-reports-design-preview-escpos-ticket" :style="getEscposPreviewTicketStyle()">
                                <div class="crud-reports-design-preview-escpos" v-html="getEscposPreviewHtml()"></div>
                              </div>
                            </div>
                          </b-col>
                        </b-row>
                      </b-card-text>
                    </b-tab>
                  </b-tabs>
                </b-card>
              </b-tab>
            </b-tabs>
          </b-col>
        </b-row>      

      </div>

      <b-card body-class="py-2">
        <div class="list-view-footer">
          <b-button type="button" variant="outline-dark" size="sm" @click="$router.go(-1)">
            <AppIcon name="chevron-double-left" class="mr-1" />Volver
          </b-button>
        </div>
      </b-card>

      <!--
      ###########################
      ######### PRINTER #########
      ###########################
      -->
      <Printer v-if="pdfPrintReport" :pdf="pdfPrintReport" />
      <EscposPrinter v-if="escposPrintContent"
                     :key="escposPrintKey"
                     :content="escposPrintContent"
                     :print-payload="escposPrintPayload" />

    </b-col>
  </b-row>
</template>

<script>
  import serviceAPI from './services'
  import Error from '@/handler/error'
  import Profiles from '@/config/profiles'
  import Modules from '@/config/modules'
  import Helper from '@/handler/helper' 
  import Session from '@/handler/session'    
  import FindObject from '@/components/inc/find/findObject'  
  import Printer from '@/components/inc/printer/printer'
  import EscposPrinter from '@/components/inc/printer/escposPrinter'
  import TabHeaderActions from '@/components/inc/tabs/TabHeaderActions'

  import PrismEditor from 'vue-prism-editor'
  import "prismjs";
  import "prismjs/themes/prism.css";

  const JsonAccordion = {
    name: 'JsonAccordion',
    props: {
      data: {
        required: true,
      },
      label: {
        type: String,
        default: '',
      },
      root: {
        type: Boolean,
        default: true,
      },
    },
    computed: {
      isObject() {
        return this.data !== null && typeof this.data === 'object'
      },
      isArray() {
        return Array.isArray(this.data)
      },
      entries() {
        if(!this.isObject) {
          return []
        }

        return Object.keys(this.data).map((key) => {
          return {
            key: key,
            value: this.data[key],
          }
        })
      },
      summary() {
        if(this.isArray) {
          return this.data.length + ' items'
        }

        if(this.isObject) {
          return this.entries.length + ' campos'
        }

        return ''
      },
      primitiveClass() {
        if(typeof this.data === 'string') {
          return 'reports-json-string'
        }

        if(typeof this.data === 'number') {
          return 'reports-json-number'
        }

        if(typeof this.data === 'boolean') {
          return 'reports-json-boolean'
        }

        if(this.data === null) {
          return 'reports-json-null'
        }

        return ''
      },
      primitiveValue() {
        if(typeof this.data === 'string') {
          return '"' + this.data + '"'
        }

        if(this.data === null) {
          return 'null'
        }

        return String(this.data)
      },
    },
    template: `
      <div class="reports-json-accordion">
        <template v-if="isObject">
          <details class="reports-json-node" :open="root">
            <summary>
              <span v-if="label" class="reports-json-key">{{ label }}</span>
              <span v-else class="reports-json-key">root</span>
              <span class="reports-json-summary">{{ isArray ? 'Array' : 'Object' }} · {{ summary }}</span>
            </summary>
            <div class="reports-json-children">
              <JsonAccordion
                v-for="item in entries"
                :key="item.key"
                :label="item.key"
                :data="item.value"
                :root="false"
              />
            </div>
          </details>
        </template>
        <template v-else>
          <div class="reports-json-leaf">
            <span class="reports-json-key">{{ label }}</span>
            <span class="reports-json-separator">:</span>
            <span :class="primitiveClass">{{ primitiveValue }}</span>
          </div>
        </template>
      </div>
    `,
  }

  export default {
    components: {
      FindObject,
      PrismEditor,      
      Printer,
      EscposPrinter,
      JsonAccordion,
      TabHeaderActions,
    },
    data: () => {
      return {      
        access: {
          module_id: Modules.REPORTES,
          profile_id: Profiles.PERSONAL,
          view_reference: 'design',
          elements: {}
        }, 
        crud: { 
          form: {
            id: 0,     
            reports: null,     
            content: '',         
            content_escpos: '',
            type_print: 'pdf',
            orientation: 'portrait',
            page: 'A4',
            font: 'Courier',
            blocked: false,
            blocked_escpos: false,
            escpos_ticket_width_mm: 80,
            escpos_margin_top_mm: 0,
            escpos_margin_right_mm: 0,
            escpos_margin_bottom_mm: 0,
            escpos_margin_left_mm: 0,
            escpos_alignment: 'left',
            name: '',
            active: true,
          },             
        },    
        filter: {
          id: 0,
        },
        jsonFilter: null,
        variablesUsedPdf: null,
        variablesUsedEscpos: null,
        contentRender: '[[no-report]]',
        contentRenderEscpos: '[[no-report]]',
        pdfPrintReport: '',
        escposPrintContent: '',
        escposPrintPayload: null,
        escposPrintKey: '',
        selectedFormat: 'pdf',
        syncingFormatTabs: false,
        activeDesignTab: 0,
        activeConfigurationTab: 0,
        activePreviewTab: 0,
        arr: {
          orientation: [
            { text: 'Vertical', value: 'portrait' },
            { text: 'Horizontal', value: 'landscape' },
          ],
          page: [
            { text: 'A4', value: 'a4' },
            { text: 'A5', value: 'a5' },
            { text: 'A6', value: 'a6' },
            { text: 'Letter', value: 'letter' },
            { text: 'Legal', value: 'legal' },
          ],
          escposWidth: [
            { text: '58 mm', value: 58 },
            { text: '80 mm', value: 80 },
          ],
          escposAlignment: [
            { text: 'Izquierda', value: 'left' },
            { text: 'Centro', value: 'center' },
            { text: 'Derecha', value: 'right' },
          ],
          fonts: [
            { text: 'Courier', value: 'courier' },
            { text: 'Arial', value: 'arial' },
            { text: 'Helvetica', value: 'helvetica' },
            { text: 'Times-Roman', value: 'times-Roman' },
            { text: 'Sans Serif', value: 'sans-serif' },
            { text: 'Symbol', value: 'symbol' },            
          ],
        }
      }
    },
    created () {     
      /* Configurar permisos de vistas y elementos */
      Helper.hasAccessView(this.access)
      /* Fin configuracion */
    }, 
    computed: {
      // Los reportes sin módulo activo (ej. Informe de Servicios sin ese
      // módulo contratado) no deben listarse acá — ver ReportsController::
      // controlByFilter en el backend, que cruza esto contra m_reports.module_id.
      activeModuleIds() {
        const modules = Session.getSession().auth.user.permissions.modules || []
        return modules.map(module => module.id)
      },
      isVersionAlternative() {
        let status = false
        if(this.crud.form.reports && this.crud.form.reports.alternative) {
          status = this.crud.form.reports.alternative
        }
        return status
      },
      canEditPdf() {
        return this.crud.form.type_print == 'pdf' || this.crud.form.type_print == 'both'
      },
      canEditEscpos() {
        return this.crud.form.type_print == 'escpos' || this.crud.form.type_print == 'both'
      }
    },
    watch: {
      activeDesignTab(newValue, oldValue) {
        this.handleFormatTabChange('design', newValue, oldValue)
      },
      activeConfigurationTab(newValue, oldValue) {
        this.handleFormatTabChange('configuration', newValue, oldValue)
      },
      activePreviewTab(newValue, oldValue) {
        this.handleFormatTabChange('preview', newValue, oldValue)
      }
    },
    methods: {      
      getFormatsOrder() {
        let formats = []

        if(this.canEditPdf) {
          formats.push('pdf')
        }

        if(this.canEditEscpos) {
          formats.push('escpos')
        }

        return formats
      },
      getFormatByTabIndex(index) {
        let formats = this.getFormatsOrder()
        return formats[index] || null
      },
      getTabIndexByFormat(format) {
        return this.getFormatsOrder().indexOf(format)
      },
      handleFormatTabChange(source, newIndex, oldIndex) {
        if(this.syncingFormatTabs) {
          return false
        }

        let format = this.getFormatByTabIndex(newIndex)
        let previousFormat = this.getFormatByTabIndex(oldIndex)

        if(previousFormat && source !== 'preview') {
          this.persistReportFormat(previousFormat)
        }

        if(!format) {
          return false
        }

        this.selectedFormat = format
        this.syncTabsToFormat(format, source)
      },
      syncTabsToFormat(format, source) {
        let index = this.getTabIndexByFormat(format)
        if(index === -1) {
          return false
        }

        this.syncingFormatTabs = true

        if(source !== 'design' && this.activeDesignTab !== index) {
          this.activeDesignTab = index
        }

        if(source !== 'configuration' && this.activeConfigurationTab !== index) {
          this.activeConfigurationTab = index
        }

        if(source !== 'preview' && this.activePreviewTab !== index) {
          this.activePreviewTab = index
        }

        this.$nextTick(() => {
          this.syncingFormatTabs = false
        })
      },
      persistReportFormat(format) {
        if(!this.crud.form.id || !format) {
          return false
        }

        let data = Object.assign({}, this.crud.form, { format: format })
        return serviceAPI.editar(data).catch(() => false)
      },
      loadReports(object) {  
        this.clearReportsAll()          

        if(object){          
          this.crud.form.reports = object          
          this.crud.form.id = object.id  

          this.crud.form.orientation = object.orientation
          this.crud.form.page = object.page
          this.crud.form.font = object.font
          this.crud.form.blocked = object.blocked
          this.crud.form.blocked_escpos = object.blocked_escpos || false
          this.crud.form.name = object.name
          this.crud.form.active = object.active
          this.crud.form.type_print = object.type_print || 'pdf'
          this.crud.form.content_escpos = object.content_user_escpos || object.content_escpos || ''
          this.crud.form.escpos_ticket_width_mm = Number(object.escpos_ticket_width_mm || 80)
          this.crud.form.escpos_margin_top_mm = Number(object.escpos_margin_top_mm || 0)
          this.crud.form.escpos_margin_right_mm = Number(object.escpos_margin_right_mm || 0)
          this.crud.form.escpos_margin_bottom_mm = Number(object.escpos_margin_bottom_mm || 0)
          this.crud.form.escpos_margin_left_mm = Number(object.escpos_margin_left_mm || 0)
          this.crud.form.escpos_alignment = object.escpos_alignment || 'left'
          this.selectedFormat = this.canEditPdf ? 'pdf' : 'escpos'
          this.syncTabsToFormat(this.selectedFormat, '')

          if(object.content_user) {
            this.crud.form.content = object.content_user            
            this.contentRender = '[[no-data]]'
          } else {
            this.crud.form.content = object.content     
            this.contentRender = '[[no-config]]'
          }              
        } else {
          this.clearReportsAll()          
        }        
      },
      filterReports() {
        if(this.crud.form.reports && !this.crud.form.reports.only_detail) {
          if(!this.filter.id || !this.crud.form.id) {          
            this.clearReportsRender()
            return false          
          }
        } else {
          this.filter.id = 0
        }

        var result = serviceAPI.mostrarJson(this.crud.form.id, this.filter.id)
        
        this.jsonFilter = null
        result.then((response) => {
          var data = response.data
          this.jsonFilter = data

          this.renderReportsPreview()
          this.variablesReports()
        })
        .catch(error => {          
          this.clearReportsRender()
          this.$awn.alert('No se encontró el registro ID: ' + this.filter.id)
        });                
      },
      variablesReports() {      
        if(this.crud.form.reports && !this.crud.form.reports.only_detail) {
          if(!this.filter.id || !this.crud.form.id) {
            this.clearReportsRender()
            return false          
          }
        } else {
          this.filter.id = 0
        }

        this.variablesUsedPdf = this.canEditPdf ? this.getVariablesUsedByContent(this.crud.form.content) : null
        this.variablesUsedEscpos = this.canEditEscpos ? this.getVariablesUsedByContent(this.crud.form.content_escpos) : null
      },
      getVariablesUsedByContent(content) {
        if(!content) {
          return null
        }

        let result = {
          variables: [],
          details: [],
        }

        let matches = content.match(/{{[^}]+}}/g)

        if(!matches || !matches.length) {
          return null
        }

        let inArray = false
        matches.forEach((value) => {
          let isArrayStart = value.indexOf('arrayStart') !== -1
          let isArrayEnd = value.indexOf('arrayEnd') !== -1

          if(isArrayStart || inArray) {
            result.details.push(value)
          } else {
            result.variables.push(value)
          }

          if(isArrayStart) {
            inArray = true
          }

          if(isArrayEnd) {
            inArray = false
          }
        })

        return result
      },
      formatJson(data) {
        if(!data) {
          return ''
        }

        let json = JSON.stringify(data, null, 2)

        json = json.replace(/&/g, '&amp;')
                   .replace(/</g, '&lt;')
                   .replace(/>/g, '&gt;')

        return json.replace(/("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+\-]?\d+)?)/g, (match) => {
          let cls = 'reports-json-number'

          if(/^"/.test(match)) {
            cls = /:$/.test(match) ? 'reports-json-key' : 'reports-json-string'
          } else if(/true|false/.test(match)) {
            cls = 'reports-json-boolean'
          } else if(/null/.test(match)) {
            cls = 'reports-json-null'
          }

          return '<span class="' + cls + '">' + match + '</span>'
        })
      },
      renderReports() { 
        if(this.crud.form.reports && !this.crud.form.reports.only_detail) {       
          if(!this.filter.id || !this.crud.form.id) {          
            this.contentRender = '[[no-data]]'
            return false          
          }
        } else {
          this.filter.id = 0
        }

        var result = serviceAPI.mostrarRender(this.crud.form.id, this.filter.id)

        result.then((response) => {
          var data = response.data 
          this.contentRender = data
        })
        .catch(error => {
          this.clearReportsRender()
          this.$awn.alert('No se encontró el registro ID: ' + this.filter.id)
        });                
      },
      renderReportsPreview() {
        if(this.canEditPdf) {
          this.renderReports()
        }

        if(this.canEditEscpos) {
          this.renderReportsEscpos()
        }
      },
      renderReportsEscpos() {
        if(this.crud.form.reports && !this.crud.form.reports.only_detail) {
          if(!this.filter.id || !this.crud.form.id) {
            this.contentRenderEscpos = '[[no-data]]'
            return false
          }
        } else {
          this.filter.id = 0
        }

        if(!this.crud.form.content_escpos) {
          this.contentRenderEscpos = '[[no-config]]'
          return false
        }

        var result = serviceAPI.mostrarRenderEscpos(this.crud.form.id, this.filter.id)

        result.then((response) => {
          var data = response.data
          this.contentRenderEscpos = data
        })
        .catch(error => {
          this.contentRenderEscpos = '[[no-data]]'
          this.$awn.alert('No se encontró el registro ID: ' + this.filter.id)
        });
      },
      saveReports(format) {
        if(!this.crud.form.id) {
          this.$awn.alert('No se seleccionó ningun reporte')
          return false          
        }

        let loader = this.$loading.show();      
        let data = Object.assign({}, this.crud.form, { format: format })

        if(format == 'escpos') {
          data.blocked_escpos = true
          this.crud.form.blocked_escpos = true
        } else {
          data.blocked = true
          this.crud.form.blocked = true
        }

        var result = serviceAPI.editar(data);
        
        result.then((response) => {          
          loader.hide()
          this.filterReports()
          this.$awn.success("Datos guardados con éxito");
        })
        .catch(error => {
          loader.hide()
          this.$awn.alert(Error.showError(error));
        })
      },
      saveReportsConfiguration(format) {
        if(!this.crud.form.id) {
          this.$awn.alert('No se seleccionó ningun reporte')
          return false          
        }

        let loader = this.$loading.show();
        let data = Object.assign({}, this.crud.form, { format: format })
        var result = serviceAPI.editarConfiguration(data);
        
        result.then((response) => {          
          loader.hide()
          this.filterReports()
          this.$awn.success("Datos guardados con éxito");
        })
        .catch(error => {
          loader.hide()
          this.$awn.alert(Error.showError(error));
        })
      },
      restoreDefaultReports(format) {
        if(!this.crud.form.id) {
          this.$awn.alert('No se seleccionó ningun reporte')
          return false          
        }

        this.$bvModal.msgBoxConfirm('¿Desea restaurar la versión original del reporte?', {
          title: 'Restaurar Reporte',
          size: 'sm',
          buttonSize: 'sm',
          okVariant: 'warning',
          okTitle: 'Restaurar',
          cancelVariant: 'outline-warning',
          cancelTitle: 'Cancelar',
          footerClass: 'p-2',
          headerBgVariant: 'warning',
          headerTextVariant: 'black',
          hideHeaderClose: false,
          centered: false
        })
        .then(value => {
          if (value) {            
            let loader = this.$loading.show();        

            if(format == 'escpos') {
              this.crud.form.content_escpos = this.crud.form.reports.content_escpos || '';
            } else {
              this.crud.form.content = this.crud.form.reports.content;
            }

            let data = Object.assign({}, this.crud.form, { format: format })
            var result = serviceAPI.editar(data);
            
            result.then((response) => {          
              loader.hide()
              this.loadReports
              this.filterReports()
              
              this.$awn.success("Reporte restaurado");
            })
            .catch(error => {
              loader.hide()
              this.$awn.alert(Error.showError(error));
            })

          }
        })
        .catch(error => {
          this.$awn.alert(Error.showError(error));
        })          
      },
      getFormatContentHTML() {   
        if( this.contentRender) {
          return this.contentRender        
        } else {
          return '[[no-config]]'
        }        
      },
      getFormatContentEscpos() {
        if( this.contentRenderEscpos) {
          return this.contentRenderEscpos
        } else {
          return '[[no-config]]'
        }
      },
      mmToPx(value) {
        let numberValue = Number(value || 0)
        return Math.round(numberValue * 3.78)
      },
      getEscposPreviewShellStyle() {
        let align = this.crud.form.escpos_alignment || 'left'
        let justifyContent = 'flex-start'

        if(align === 'center') {
          justifyContent = 'center'
        } else if(align === 'right') {
          justifyContent = 'flex-end'
        }

        return {
          display: 'flex',
          justifyContent: justifyContent,
          alignItems: 'flex-start',
          minHeight: '560px',
          overflow: 'auto',
          padding: '16px',
        }
      },
      getEscposPreviewScale() {
        return 1
      },
      getEscposPreviewTicketStyle() {
        return {
          width: this.mmToPx(this.crud.form.escpos_ticket_width_mm || 80) + 'px',
          minWidth: this.mmToPx(this.crud.form.escpos_ticket_width_mm || 80) + 'px',
          maxWidth: 'none',
          boxSizing: 'border-box',
          transform: 'scale(' + this.getEscposPreviewScale() + ')',
          transformOrigin: 'top left',
          paddingTop: this.mmToPx(this.crud.form.escpos_margin_top_mm || 0) + 'px',
          paddingRight: this.mmToPx(this.crud.form.escpos_margin_right_mm || 0) + 'px',
          paddingBottom: this.mmToPx(this.crud.form.escpos_margin_bottom_mm || 0) + 'px',
          paddingLeft: this.mmToPx(this.crud.form.escpos_margin_left_mm || 0) + 'px',
          textAlign: this.crud.form.escpos_alignment || 'left',
          letterSpacing: '0',
          fontFamily: 'Courier New, Courier, monospace',
          fontSize: '10px',
          lineHeight: '1.1',
          overflowX: 'auto',
        }
      },
      escapeHtml(value) {
        return String(value || '')
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&#39;')
      },
      getEscposPreviewHtml() {
        let content = this.getFormatContentEscpos()

        if(!content || content == '[[no-config]]' || content == '[[no-report]]' || content == '[[no-data]]') {
          return ''
        }

        let lines = []
        let align = this.crud.form.escpos_alignment || 'left'
        let bold = false
        let currentLine = {
          align: align,
          segments: [],
        }
        let currentSegment = ''
        let currentSegmentBold = bold

        const pushSegment = () => {
          if(currentSegment !== '') {
            currentLine.segments.push({
              text: currentSegment,
              bold: currentSegmentBold,
            })
            currentSegment = ''
          }
        }

        const pushLine = () => {
          pushSegment()
          lines.push({
            align: currentLine.align,
            segments: currentLine.segments.slice(),
          })
          currentLine = {
            align: align,
            segments: [],
          }
        }

        for(let i = 0; i < content.length; i++) {
          let charCode = content.charCodeAt(i)

          // ESC (27). Algunas secuencias son de 2 bytes (ESC @), otras de 3.
          if(charCode === 27 && i + 1 < content.length) {
            let command = content.charCodeAt(i + 1)

            if(command === 64) { // ESC @ — inicializar impresora
              i += 1
              continue
            }

            if(i + 2 >= content.length) {
              i += 1
              continue
            }

            let value = content.charCodeAt(i + 2)

            if(command === 97) { // ESC a — alineación
              if(value === 0) align = 'left'
              else if(value === 1) align = 'center'
              else if(value === 2) align = 'right'
              currentLine.align = align
              i += 2
              continue
            }

            if(command === 69) { // ESC E — negrita
              pushSegment()
              bold = value === 1
              currentSegmentBold = bold
              i += 2
              continue
            }

            if(command === 100) { // ESC d — avance de líneas (feed)
              i += 2
              continue
            }

            // Cualquier otra secuencia ESC de 3 bytes: saltar
            i += 2
            continue
          }

          // GS (29) — secuencias de 3 bytes
          if(charCode === 29 && i + 2 < content.length) {
            i += 2
            continue
          }

          if(content[i] === '\n') {
            pushLine()
            continue
          }

          if(content[i] === '\r') {
            continue
          }

          // Saltar cualquier otro byte de control no imprimible
          if(charCode < 32 && charCode !== 9) {
            continue
          }

          currentSegment += content[i]
        }

        pushLine()

        return lines.map((line) => {
          let html = line.segments.map((segment) => {
            let text = this.escapeHtml(segment.text)
            return segment.bold ? '<strong>' + text + '</strong>' : text
          }).join('')

          return '<div style="text-align:' + line.align + ';white-space:pre;display:block;min-width:max-content;">' + html + '</div>'
        }).join('')
      },
      hasDefaultDesign(format) {
        if(!this.crud.form.reports) {
          return false
        }

        if(format == 'escpos') {
          return this.crud.form.reports.content_escpos !== undefined && this.crud.form.reports.content_escpos !== ''
        }

        return this.crud.form.reports.content !== undefined && this.crud.form.reports.content !== ''
      },
      getHelp(value) {
        var arrHelp = []
        arrHelp[0] = `Para agregar una variable al reporte, se tiene que escribir el nombre de la variable entre dos llaves. EJ: {{variable}}`
        arrHelp[1] = `La variable, antes de las llave de apertura y luego del cierre de llaves, tiene que tener un espacio.`
        arrHelp[2] = `Para acceder a los objetos hijos, se puede hacer de la siguiente manera: {{customer.name}}`
        arrHelp[3] = `Se puede acceder con el punto (.) hasta la ultima propiedad disponible del objeto.`
        arrHelp[4] = `Para darle un formato al dato desde la variable, seguido al nombre de la variable, agregar el caracter pleca (|) y luego el tipo de dato. EJ: {{variable|date}}`        
        arrHelp[5] = `Los formatos de datos disponibles actualmente son: date, currency, int, decimal, pointSales, numberInvoice, si-no, image, concatLeft[valor], concatRight[valor], tablePriceList[<fields>],tablePriceListWithoutHeader[<fields>], eval(<formula>), if(true,false,tag,class), field-valid(compare,true,false,format,tag,class).`
        arrHelp[6] = `Para agregar un bloque con repetición, arriba del bloque a repetir, (se mantiene la sintaxis de las variables) agregar de la siguiente manera: {{details|arrayStart}} y al final del bloque {{details|arrayEnd}}`
        arrHelp[7] = `Si en el raíz, son datos de repetición, puede hacer referencia de la sigueinte manera: {{-|arrayStart}} y al final del bloque {{-|arrayEnd}}`
        arrHelp[8] = `Dentro del bloque con repetición, se mantiene la sintaxis de las variables, Partir como base de la primer propiedad hija declarada en la variable de repeticion.`
        arrHelp[9] = `El formato 'eval', sirve para ejecutar una sentencia y poder realizar cálculos en los reportes. Si queremos llamar a un campo del registro, usamos field['<campo>'], la formula no debe contener espacios. Por ejemplo: {{price_unit|eval(field['price_unit']+(field['amount_iva']/field['quantity']))}}`
        arrHelp[10] = `El formato 'tablePriceList' y 'tablePriceListWithoutHeader', sirve para mostrar un detalle de los productos con sus precios. Por defecto ['code:15,name:50,desc:10,price:15,days_update:10']. Se define, [<campo>:<ancho>]. Puede omitir los campos que no quiere mostrar`
        arrHelp[11] = `El formato 'if' es un condicional, evalua si esta seteado el campo. Si es True, devuelve el primer parametro (un string o si se pone el nombre del campo, devuelve su valor), False (puede dejar vacio para que no retorne nada), el tercer parámetro envuelve el resultado en una etiqueta html y el cuarto parámetro aplica una clase de estilo`
        arrHelp[12] = `El formato 'field-valid' es un condicional, evalua si el campo cumple con la condicion. Si es True, devuelve el segundo parametro, si es False devulve el tercer parametro. El cuarto parámetro aplica un formato a la devolucion (ej: currency). El quinto parametro envuelve el resultado en una etiqueta html y el sexto parámetro aplica una clase de estilo`
        
        return arrHelp[value]
      },
      generateReports() {
        if(this.crud.form.reports && !this.crud.form.reports.only_detail) {       
          if(!this.filter.id || !this.crud.form.id) {
            this.contentRender = '[[no-data]]'
            this.$awn.alert('No se encontraron datos para imprimir')
            return false          
          }
        } else {
          this.filter.id = 0
        }
        
        var result = serviceAPI.generate({
          reportsID: this.crud.form.id,
          regID: this.filter.id
        })

        result.then((response) => {
          var data = response.data      
          this.pdfPrintReport = data            
        })
        .catch(error => {
          this.$awn.alert(Error.showError(error))
        });             
      },
      generateReportsEscpos() {
        if(this.crud.form.reports && !this.crud.form.reports.only_detail) {
          if(!this.filter.id || !this.crud.form.id) {
            this.contentRenderEscpos = '[[no-data]]'
            this.$awn.alert('No se encontraron datos para imprimir')
            return false
          }
        }

        let content = this.getFormatContentEscpos()

        if(content == '[[no-config]]' || content == '[[no-report]]' || content == '[[no-data]]') {
          this.$awn.alert('No se encontraron datos ESC/POS para imprimir')
          return false
        }

        if(this.jsonFilter) {
          this.prepareEscposPrint(content, this.jsonFilter)
          return true
        }

        var result = serviceAPI.mostrarJson(this.crud.form.id, this.filter.id)

        result.then((response) => {
          this.prepareEscposPrint(content, response.data)
        })
        .catch(error => {
          this.$awn.alert(Error.showError(error))
        })
      },
      prepareEscposPrint(content, data) {
        this.escposPrintKey = Math.random().toString(36).slice(2,8)
        this.escposPrintPayload = data
        this.escposPrintContent = content
      },
      clearReportsAll() {
        this.jsonFilter = null
        this.variablesUsedPdf = null
        this.variablesUsedEscpos = null
        this.contentRender = '[[no-report]]',
        this.contentRenderEscpos = '[[no-report]]',
        this.pdfPrintReport = ''
        this.escposPrintContent = ''
        this.escposPrintPayload = null
        this.escposPrintKey = ''
        this.selectedFormat = 'pdf'

        this.crud.form.reports = null
        this.crud.form.id = 0
        this.crud.form.orientation = 'portrait'
        this.crud.form.page = 'A4'
        this.crud.form.font = 'Courier'
        this.crud.form.content = ''
        this.crud.form.content_escpos = ''
        this.crud.form.type_print = 'pdf'
        this.crud.form.blocked = false
        this.crud.form.blocked_escpos = false
        this.crud.form.escpos_ticket_width_mm = 80
        this.crud.form.escpos_margin_top_mm = 0
        this.crud.form.escpos_margin_right_mm = 0
        this.crud.form.escpos_margin_bottom_mm = 0
        this.crud.form.escpos_margin_left_mm = 0
        this.crud.form.escpos_alignment = 'left'
        this.activeDesignTab = 0
        this.activeConfigurationTab = 0
        this.activePreviewTab = 0
      },
      clearReportsRender() {
        this.jsonFilter = null
        this.variablesUsedPdf = null
        this.variablesUsedEscpos = null
        this.contentRender = '[[no-data]]',
        this.contentRenderEscpos = '[[no-data]]',
        this.pdfPrintReport = ''
      }
    } 
  }
</script>
<style>
  .reports-design-workspace {
    padding: 0;
    margin-bottom: 20px;
  }
  .reports-design-selector-panel {
    background: var(--app-color-surface);
    border: 1px solid var(--app-color-border);
    border-radius: 4px;
    box-shadow: var(--app-shadow-sm);
  }
  .reports-design-selector-panel {
    padding: 14px 16px;
  }
  .reports-design-empty-card {
    margin-top: 16px;
  }
  .reports-design-layout {
    margin-top: 16px;
  }
  .reports-design-layout > [class*="col-"] > .tabs {
    height: 735px;
    display: flex;
    flex-direction: column;
    min-height: 0;
  }
  .reports-design-layout > [class*="col-"] > .tabs > .tab-content {
    min-height: 0;
    flex: 1 1 auto;
    overflow: auto;
  }
 .crud-reports-design-preview {
    background: var(--app-color-canvas);
    padding: 15px;
    height: 590px;
  }
  .crud-reports-design-preview .crud-reports-design-preview-html {
    background: var(--app-color-surface);
    height: 560px;
    padding: 15px;
    overflow: overlay;
  }
  .reports-design-config-section-title {
    color: var(--app-color-text);
    font-size: 13px;
    font-weight: 700;
    margin-bottom: 4px;
    text-transform: uppercase;
  }
  .crud-reports-design-preview-escpos-shell {
    background: var(--app-color-canvas);
    height: 590px;
  }
  .crud-reports-design-preview-escpos-ticket {
    background: var(--app-color-surface);
    border: 1px solid var(--app-color-border);
    box-shadow: var(--app-shadow-sm);
    color: var(--app-color-text);
    min-height: 100%;
    overflow: auto;
  }
  .crud-reports-design-preview-escpos {
    background: transparent;
    font-family: Courier, monospace;
    font-size: 10px;
    line-height: 1.1;
    margin: 0;
    min-height: 100%;
    overflow: visible;
    padding: 0;
    white-space: pre;
    width: 100%;
    min-width: 100%;
  }
  .crud-reports-design-code-editor {
    height: 575px;
  }
  .crud-reports-design-code-editor pre {
    height: 575px;
  }  
  .reports-design-button-filter {
    margin-top: 30px;
  }
  .reports-design-pre-json {
    overflow: auto;    
    height: 625px;
    width: 100%;
    min-width: unset;
  }
  .reports-design-json-code {
    background: var(--app-color-surface-subtle);
    border: 1px solid var(--app-color-border);
    border-radius: 4px;
    color: var(--app-color-text);
    font-family: Courier, monospace;
    font-size: 12px;
    line-height: 1.45;
    margin: 0;
    padding: 12px;
    white-space: pre;
  }
  /* Resaltado de sintaxis JSON (por tipo de dato): excepción documentada en
     docs/design-system/color-palette/README.md ("colores monoespaciados o
     resaltado de código, si pertenecen a un editor"). */
  .reports-json-key {
    color: #005cc5;
    font-weight: 700;
  }
  .reports-json-string {
    color: #22863a;
  }
  .reports-json-number {
    color: #b31d28;
  }
  .reports-json-boolean {
    color: #6f42c1;
    font-weight: 700;
  }
  .reports-json-null {
    color: #6a737d;
    font-style: italic;
  }
  .reports-json-accordion {
    background: var(--app-color-surface-subtle);
    border: 1px solid var(--app-color-border);
    border-radius: 4px;
    color: var(--app-color-text);
    font-family: Courier, monospace;
    font-size: 12px;
    line-height: 1.45;
    padding: 10px;
  }
  .reports-json-accordion .reports-json-accordion {
    border: 0;
    border-radius: 0;
    background: transparent;
    padding: 0;
  }
  .reports-json-node {
    margin: 2px 0;
  }
  .reports-json-node summary {
    cursor: pointer;
    padding: 4px 2px;
    user-select: none;
  }
  .reports-json-node summary:hover {
    background: var(--app-color-primary-soft);
  }
  .reports-json-summary {
    color: var(--app-color-text-muted);
    margin-left: 6px;
  }
  .reports-json-children {
    border-left: 1px solid var(--app-color-border);
    margin-left: 8px;
    padding-left: 12px;
  }
  .reports-json-leaf {
    padding: 3px 2px;
    white-space: pre-wrap;
    word-break: break-word;
  }
  .reports-json-separator {
    color: var(--app-color-text-muted);
    margin: 0 4px;
  }
  .reports-design-vertical-tabs {
    display: flex;
    flex-wrap: nowrap;
    align-items: stretch;
    height: 625px;
  }
  .reports-design-vertical-tabs > .card-header {
    flex: 0 0 135px;
    border-bottom: 0;
    border-right: 1px solid var(--app-color-border);
  }
  .reports-design-vertical-tabs > .tab-content {
    flex: 1 1 auto;
    width: auto;
    min-width: 0;
    overflow: auto;
  }
  .reports-design-vertical-tabs > .tab-content > .tab-pane {
    height: 100%;
  }
  .reports-design-used-accordion {
    display: flex;
    flex-direction: column;
    gap: 8px;
    max-height: 625px;
    overflow: auto;
  }
  .reports-design-used-card {
    border: 1px solid var(--app-color-border);
    border-radius: 4px;
    overflow: hidden;
  }
  .reports-design-used-header {
    display: flex;
    align-items: center;
    gap: 6px;
    border: 0;
    border-radius: 0;
    font-weight: 700;
    justify-content: flex-start;
    text-align: left;
  }
  .reports-design-used-card .card-body {
    padding: 10px;
  }
  .reports-design-used-card .reports-design-pre-json {
    height: 500px;
  }
  .reports-design-help {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding-top: 8px;
    max-height: 625px;
    overflow: auto;
  }
  .reports-design-help-section {
    border: 1px solid var(--app-color-border);
    border-radius: 4px;
    background: var(--app-color-surface);
    padding: 12px;
  }
  .reports-design-help-title {
    display: flex;
    align-items: center;
    gap: 6px;
    font-weight: 700;
    margin-bottom: 8px;
    color: var(--app-color-text);
  }
  .reports-design-help-toggle {
    cursor: pointer;
    user-select: none;
    justify-content: space-between;
    padding: 4px 2px;
    border-radius: 4px;
    transition: background 0.15s;
  }
  .reports-design-help-toggle:hover {
    background: var(--app-color-primary-soft);
  }
  .reports-design-help-chevron {
    margin-left: auto;
    transition: transform 0.2s;
    flex-shrink: 0;
  }
  .collapsed .reports-design-help-chevron {
    transform: rotate(-90deg);
  }
  .reports-design-help-subtitle {
    margin-top: 12px;
    padding-top: 10px;
    border-top: 1px solid var(--app-color-border);
    font-weight: 700;
    color: var(--app-color-text-secondary);
  }
  .reports-design-help-item {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 8px 0;
    border-top: 1px solid var(--app-color-border);
  }
  .reports-design-help-item code {
    width: fit-content;
    white-space: normal;
    display: inline-block;
    padding: 3px 6px;
    border: 1px solid var(--app-color-border);
    border-radius: 4px;
    background: var(--app-color-surface-subtle);
    color: var(--app-color-text);
    font-size: 12px;
    line-height: 1.35;
  }
  .reports-design-help-format-list {
    display: grid;
    grid-template-columns: 1fr;
    gap: 6px;
    margin-top: 10px;
  }
  .reports-design-help-format {
    display: grid;
    grid-template-columns: minmax(95px, auto) 1fr;
    gap: 8px;
    align-items: start;
    padding: 6px 0;
    border-top: 1px solid var(--app-color-border);
  }
  .reports-design-help-format code {
    width: fit-content;
    padding: 3px 6px;
    border: 1px solid var(--app-color-border);
    border-radius: 4px;
    background: var(--app-color-surface-subtle);
    color: var(--app-color-text);
    font-size: 12px;
    line-height: 1.35;
  }
  .reports-design-help-note {
    margin-top: 8px;
    padding: 8px;
    border-left: 3px solid var(--app-color-info);
    background: var(--app-color-info-soft);
    color: var(--app-color-primary-hover);
  }
</style>
